import { ReactNode, useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, ClipboardList, Package, PackageCheck, RefreshCw, Send, ShieldCheck, Warehouse } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { AdminErpDashboard, AdminUser } from "../admin/api";
import { ErpApiError, getCurrentErpUser, getErpDashboard, logoutErp } from "./api";
import ErpLayout from "./ErpLayout";

function count(value?: number | null) {
  return `${Number(value || 0).toLocaleString("zh-TW")} 筆`;
}

function money(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`;
}

function MetricCard({ href, icon, label, note, tone = "default", value }: { href?: string; icon: ReactNode; label: string; note?: string; tone?: "default" | "warning" | "danger"; value: string }) {
  const toneClass = tone === "danger" ? "border-l-[#a33a2f]" : tone === "warning" ? "border-l-[#b7791f]" : "border-l-[#315746]";
  const iconClass = tone === "danger" ? "bg-[#fdebea] text-[#8a1f1f]" : tone === "warning" ? "bg-[#fff5df] text-[#8a5b14]" : "bg-[#edf5f0] text-[#315746]";
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-[#69776f]">{label}</p>
          <p className="mt-2 break-words text-[28px] font-extrabold leading-tight text-[#203d32]">{value}</p>
        </div>
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${iconClass}`}>{icon}</span>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        {note ? <p className="break-words text-sm font-semibold text-[#7b877f]">{note}</p> : <span />}
        {href ? <ArrowRight className="shrink-0 text-[#315746]" size={17} /> : null}
      </div>
    </>
  );

  if (href) {
    return (
      <a className={`block rounded-md border border-l-4 border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)] transition hover:-translate-y-0.5 hover:border-[#b8c8bf] hover:shadow-[0_12px_28px_rgba(32,61,50,.08)] ${toneClass}`} href={href}>
        {content}
      </a>
    );
  }

  return (
    <article className={`rounded-md border border-l-4 border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)] ${toneClass}`}>
      {content}
    </article>
  );
}

function ActionCard({ href, icon, label, note, value, warning }: { href: string; icon: ReactNode; label: string; note: string; value: string; warning?: boolean }) {
  return (
    <a className={`flex min-h-[116px] flex-col justify-between rounded-md border bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(32,61,50,.08)] ${warning ? "border-[#efd29a]" : "border-[#dfe6e1]"}`} href={href}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-[#69776f]">{label}</p>
          <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#59665f]">{note}</p>
        </div>
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${warning ? "bg-[#fff5df] text-[#8a5b14]" : "bg-[#edf5f0] text-[#315746]"}`}>{icon}</span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="break-words text-xl font-extrabold text-[#203d32]">{value}</span>
        <span className="inline-flex min-h-9 shrink-0 items-center gap-1 rounded-md bg-[#315746] px-3 text-sm font-extrabold text-white">
          處理 <ArrowRight size={15} />
        </span>
      </div>
    </a>
  );
}

function FlowCard({ href, index, label, countValue }: { href?: string; index: number; label: string; countValue: number }) {
  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#edf5f0] font-extrabold text-[#315746]">{index}</span>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-extrabold text-[#59665f]">{count(countValue)}</span>
      </div>
      <div className="mt-3 flex min-h-[44px] items-end justify-between gap-2">
        <p className="break-words text-sm font-extrabold text-[#203d32]">{label}</p>
        {href ? <ArrowRight className="shrink-0 text-[#315746]" size={16} /> : null}
      </div>
    </>
  );

  if (href) {
    return (
      <a className="block rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4 transition hover:border-[#b8c8bf] hover:bg-white" href={href}>
        {content}
      </a>
    );
  }

  return (
    <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
      {content}
    </div>
  );
}

const flowTargetMap: Record<string, string> = {
  PendingEngineering: "/erp/outbound-orders/new",
  WaitingPurchase: "/erp/purchase-orders/new",
  PreparingOutbound: "/erp/outbound-orders/new",
  ShippedToStore: "/erp/outbound-orders?Status=Shipped",
  ReadyForPickup: "/erp/outbound-orders?Status=Arrived",
  Completed: "/erp/outbound-orders?Status=Arrived",
};

export default function ErpDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [dashboard, setDashboard] = useState<AdminErpDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentErpUser();
      setUser(me.User);
      const data = await getErpDashboard();
      setDashboard(data);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "ERP 工作台載入失敗。");
      if (error instanceof ErpApiError && error.status === 401) navigate("/erp-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logoutErp().catch(() => undefined);
    navigate("/erp-login", { replace: true });
  }

  const metrics = dashboard?.Metrics;

  return (
    <ErpLayout title="ERP 工作台" user={user} onLogout={handleLogout}>
      <div className="w-full max-w-none">
        <section className="mb-5 rounded-md border border-[#dfe6e1] bg-white p-5 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-extrabold text-[#69776f]">ERP 現場作業入口</p>
              <h2 className="mt-2 break-words text-2xl font-extrabold text-[#203d32]">採購、庫存、工程備貨與出庫狀態總覽</h2>
              <p className="mt-2 max-w-4xl text-sm font-semibold leading-7 text-[#59665f]">承接門市訂單後，可從這裡進入採購、到貨驗收、工程檢查、庫存、揀貨與出庫到門市等每日作業。</p>
            </div>
            <div className="rwd-action-grid xl:w-[180px]">
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60" disabled={loading} onClick={() => load()} type="button">
                <RefreshCw size={18} />
                重新整理
              </button>
            </div>
          </div>
        </section>

        {message ? <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-bold text-[#8a1f1f]">{message}</div> : null}
        {loading && !dashboard ? <div className="rounded-md border border-[#dfe6e1] bg-white p-10 text-center font-semibold text-[#59665f]">載入中...</div> : null}

        {metrics ? (
          <div className="space-y-5">
            <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[.14em] text-[#7b877f]">Daily Tasks</p>
                  <h2 className="mt-1 text-base font-extrabold text-[#203d32]">今日 ERP 待辦入口</h2>
                </div>
                <span className="text-sm font-bold text-[#7b877f]">依門市訂單、採購、庫存與出庫狀態即時彙整</span>
              </div>
              <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
                <ActionCard href="/erp/purchase-orders/new" icon={<ClipboardList size={20} />} label="建立採購單" note="處理待廠商採購、一般補貨、耗材或備料採購。" value={count(metrics.WaitingPurchase)} warning={metrics.WaitingPurchase > 0} />
                <ActionCard href="/erp/purchase-receipts" icon={<ShieldCheck size={20} />} label="採購入庫驗收" note="國外或供應商到貨後，先登錄、檢驗，再放行入庫。" value={count(metrics.PendingPurchaseReceipts)} warning={metrics.PendingPurchaseReceipts > 0} />
                <ActionCard href="/erp/outbound-orders/new" icon={<Send size={20} />} label="建立出庫單" note="依門市訂單揀貨、選序號、工程檢查後出庫。" value={count(metrics.PreparingOutbound)} warning={metrics.PreparingOutbound > 0} />
                <ActionCard href="/erp/payables" icon={<AlertTriangle size={20} />} label="請款與付款" note="查看待付款、逾期付款與供應商對帳狀態。" value={money(metrics.PayableBalanceAmount)} warning={metrics.PendingPayables > 0 || metrics.OverduePayables > 0} />
              </div>
            </section>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard href="/erp/outbound-orders/new" icon={<ClipboardList size={22} />} label="待工程部備貨" value={count(metrics.PendingEngineering)} note="總部確認後交由工程處理" />
              <MetricCard href="/erp/purchase-orders/new" icon={<Package size={22} />} label="待廠商採購" value={count(metrics.WaitingPurchase)} note="無庫存或需向廠商採購" tone={metrics.WaitingPurchase > 0 ? "warning" : "default"} />
              <MetricCard href="/erp/outbound-orders/new" icon={<Send size={22} />} label="待出庫" value={count(metrics.PreparingOutbound)} note="準備出庫到指定門市" />
              <MetricCard href="/erp/outbound-orders?Status=Arrived" icon={<Warehouse size={22} />} label="已出庫到店 / 可取件" value={count(metrics.ShippedToStore + metrics.ReadyForPickup)} note="門市可通知客戶取件" />
              <MetricCard href="/erp/inventory" icon={<PackageCheck size={22} />} label="SKU 數" value={count(metrics.SkuCount)} note={`既有庫存 ${metrics.LegacyStockTotal.toLocaleString("zh-TW")} 件`} />
              <MetricCard href="/erp/inventory?StockLevel=available" icon={<Warehouse size={22} />} label="ERP 現貨" value={`${metrics.ErpInStockTotal.toLocaleString("zh-TW")} 件`} note={`保留 ${metrics.ErpReservedTotal.toLocaleString("zh-TW")} 件`} />
              <MetricCard href="/erp/inventory?StockLevel=low" icon={<AlertTriangle size={22} />} label="低庫存" value={count(metrics.LowStockCount)} tone={metrics.LowStockCount > 0 ? "warning" : "default"} />
              <MetricCard href="/erp/inventory?StockLevel=out" icon={<AlertTriangle size={22} />} label="無庫存" value={count(metrics.OutOfStockCount)} tone={metrics.OutOfStockCount > 0 ? "danger" : "default"} />
              <MetricCard href="/erp/purchase-orders" icon={<AlertTriangle size={22} />} label="逾期採購" value={count(metrics.OverduePurchaseOrders)} note="已超過預計到貨日" tone={metrics.OverduePurchaseOrders > 0 ? "warning" : "default"} />
              <MetricCard href="/erp/purchase-receipts" icon={<ShieldCheck size={22} />} label="待驗收" value={count(metrics.PendingPurchaseReceipts)} note="到貨後尚未驗收放行" tone={metrics.PendingPurchaseReceipts > 0 ? "warning" : "default"} />
              <MetricCard href="/erp/purchase-receipts" icon={<AlertTriangle size={22} />} label="驗收異常" value={count(metrics.ReceiptExceptions)} note="待退貨、換貨、補件或結案" tone={metrics.ReceiptExceptions > 0 ? "danger" : "default"} />
              <MetricCard href="/erp/payables" icon={<ClipboardList size={22} />} label="待付款 / 逾期" value={`${metrics.PendingPayables.toLocaleString("zh-TW")} / ${metrics.OverduePayables.toLocaleString("zh-TW")} 筆`} note={money(metrics.PayableBalanceAmount)} tone={metrics.OverduePayables > 0 ? "danger" : metrics.PendingPayables > 0 ? "warning" : "default"} />
            </div>

            <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-extrabold text-[#203d32]">門市訂單 ERP 流程</h2>
                <span className="text-sm font-bold text-[#7b877f]">目前以門市訂單狀態同步</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
                {dashboard.StoreOrderFlow.map((item, index) => <FlowCard countValue={item.Count} href={flowTargetMap[item.Key]} index={index + 1} key={item.Key} label={item.Label} />)}
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </ErpLayout>
  );
}
